import type {
  HubDeletedExampleStream,
  HubExampleStream,
  HubResource,
} from '@robovision/hub'
import {
  addOrReplaceByKey,
  DateTemplates,
  formatDate,
  HttpStatusCode,
  wait,
} from '@robovision/rvai-utils'
import { saveAs } from 'file-saver'
import { defineStore } from 'pinia'
import {
  reactive,
  shallowReadonly,
  toRefs,
} from 'vue'
import { ApiErrorId } from '~domain/ApiErrorId'
import { CameraConfiguration } from '~domain/cameras/CameraConfiguration'
import type { CameraConfigurationData } from '~domain/cameras/CameraConfigurationData'
import {
  CameraConfigurationCameraKind,
  CameraConfigurationKindIds,
} from '~domain/cameras/CameraConfigurationKind'
import { hubApi } from '~services/api'
import { acceptStatus } from '~utils/api'
import { useExampleDefinitionsStore } from './useExampleDefinitionStore'


function isValidConfigKind (kind?: string): boolean {
  return kind === CameraConfigurationKindIds.Camera || kind === CameraConfigurationKindIds.Datapool
}

export const useCameraConfigurationsStore = defineStore('cameraConfigurations', () => {
  const state = reactive({
    cameraConfigurations: [] as CameraConfiguration[],
    cameraConfigurationsCalibrationResource: new Map<string, HubResource>(),
    downloading: false as boolean,
    loading: false as boolean,
    saving: false as boolean,
  })

  const actions = {
    async createCameraConfiguration (data: CameraConfigurationData): Promise<void> {
      state.saving = true
      try {
        const { getExampleStreamViewTemplates } = useExampleDefinitionsStore()
        const { data: response } = await hubApi.exampleStreams.createExampleStream(await data.toCreateBody())
        const newCameraConfiguration = CameraConfiguration.fromResponse(response)
        newCameraConfiguration.setViewTemplates(
          getExampleStreamViewTemplates(newCameraConfiguration.definitionId),
        )
        state.cameraConfigurations = addOrReplaceByKey(state.cameraConfigurations, newCameraConfiguration, 'id')
      } catch (error) {
        console.error(error)
      } finally {
        state.saving = false
      }
    },

    async deleteCameraConfiguration (config: CameraConfiguration): Promise<void> {
      state.loading = true
      try {
        await hubApi.exampleStreams.deleteExampleStream(config.id, { hardDelete: true })
        state.cameraConfigurations = state.cameraConfigurations.filter(c => c.id !== config.id)
      } finally {
        state.loading = false
      }
    },

    async downloadCameraConfiguration (
      cameraConfigurationId?: string,
      cameraId?: string,
      configName?: string,
    ): Promise<void> {
      state.downloading = true
      try {
        if (!cameraConfigurationId || !cameraId || !configName) {
          throw new Error('Missing required parameters')
        }

        const response = await hubApi.exampleStreams.getCameraConfigData(
          cameraConfigurationId,
          cameraId,
          { format: 'arraybuffer' },
        )

        if (response.status === HttpStatusCode.NOT_FOUND) {
          throw new Error('Failed to download camera configuration')
        }

        const data = response.data
        const blob = new Blob([data], { type: 'octet/stream' })
        saveAs(blob, configName)
      } finally {
        state.downloading = false
      }
    },

    async downloadValidationLogs (
      config: CameraConfiguration,
      repeat = 0,
      repeatDelay = 1000,
    ): Promise<void> {
      state.downloading = true
      try {
        const response = await hubApi.exampleStreams.getValidationLogs(
          config.id,
          {
            format: 'arraybuffer',
            validateStatus: acceptStatus(HttpStatusCode.NOT_FOUND),
          },
        )

        // check for logs every second
        if (response.status === HttpStatusCode.NOT_FOUND) {
          if (repeat) {
            await wait(repeatDelay)
            await this.downloadValidationLogs(config, repeat - 1, repeatDelay)
            return
          } else {
            throw new Error('Failed to download validation logs')
          }
        }

        const blob = new Blob([response.data], { type: 'octet/stream' })
        const date = formatDate(new Date(), DateTemplates.Long)
        const fileExtension = response.headers['content-disposition']
          ?.split('filename=')
          .pop()
          .replaceAll('"', '')
          .split('.')
          .pop() || 'log'
        const fileName = `validation_logs_${date}.${fileExtension}`

        saveAs(blob, fileName)
      } finally {
        state.downloading = false
      }
    },

    handleCameraConfiguration (data: HubExampleStream): void {
      if (!isValidConfigKind(data.config.kind)) return

      const { getExampleStreamViewTemplates } = useExampleDefinitionsStore()

      const cameraConfiguration = CameraConfiguration.fromResponse(data)
      cameraConfiguration.setViewTemplates(
        getExampleStreamViewTemplates(cameraConfiguration.definitionId),
      )
      state.cameraConfigurations = addOrReplaceByKey(state.cameraConfigurations, cameraConfiguration, 'id')
    },

    handleDeleteCameraConfigurationEvent (deletedCameraConfiguration: HubDeletedExampleStream): void {
      state.cameraConfigurations = state.cameraConfigurations.filter(c => c.id !== deletedCameraConfiguration.id)
    },

    async loadCameraConfiguration (id: string): Promise<void> {
      state.loading = true
      try {
        const { getExampleStreamViewTemplates } = useExampleDefinitionsStore()
        const { data } = await hubApi.exampleStreams.getExampleStream(id)

        if (!isValidConfigKind(data.config.kind)) return

        const cameraConfiguration = CameraConfiguration.fromResponse(data)
        cameraConfiguration.setViewTemplates(
          getExampleStreamViewTemplates(cameraConfiguration.definitionId),
        )

        state.cameraConfigurations = addOrReplaceByKey(
          state.cameraConfigurations,
          cameraConfiguration,
          'id',
        )
      } finally {
        state.loading = false
      }
    },

    async loadCameraConfigurationCalibrationResource (
      cameraConfigurationId: string,
      projectId: string,
    ): Promise<HubResource|undefined> {
      try {
        const { data } = await hubApi.alpha.listProjectCalibrationResourcesPerCameraConfig(
          projectId,
          cameraConfigurationId,
          undefined,
          {
            validateStatus: acceptStatus(HttpStatusCode.NOT_FOUND),
          },
        )

        if (data.id === ApiErrorId.CalibrationResourcesNotFound) {
          state.cameraConfigurationsCalibrationResource.delete(cameraConfigurationId)
          return undefined
        }

        state.cameraConfigurationsCalibrationResource.set(cameraConfigurationId, data)
        return data
      } catch (e) {
        console.error(e)
      }
    },

    async loadCameraConfigurationFiles (cameraConfiguration: CameraConfiguration): Promise<void> {
      state.loading = true
      try {
        if (cameraConfiguration.config.kind !== CameraConfigurationKindIds.Camera) return
        await Promise.all((cameraConfiguration.config as CameraConfigurationCameraKind).cameras.map(async c => {
          const response = await hubApi.exampleStreams.getCameraConfigData(
            cameraConfiguration.id,
            c.id,
            { format: 'arraybuffer' },
          )
          c.configFile = new File([response.data], c.configName)
        }))

        state.cameraConfigurations = addOrReplaceByKey(
          state.cameraConfigurations,
          cameraConfiguration,
          'id',
        )
      } finally {
        state.loading = false
      }
    },

    async loadCameraConfigurations (filter: string[] = []): Promise<void> {
      state.loading = true
      try {
        const { getExampleStreamViewTemplates } = useExampleDefinitionsStore()
        const { data } = await hubApi.exampleStreams.listExampleStreams({
          filter,
        })

        const cameraConfigurations = data.items
          .filter(c => isValidConfigKind(c.config.kind))
          .map(CameraConfiguration.fromResponse)
        cameraConfigurations.forEach(cameraConfiguration => {
          cameraConfiguration.setViewTemplates(
            getExampleStreamViewTemplates(cameraConfiguration.definitionId),
          )
        })

        state.cameraConfigurations = cameraConfigurations
      } finally {
        state.loading = false
      }
    },

    setState (params: Partial<typeof state>): void {
      Object.assign(state, params)
    },

    async updateCameraConfiguration (
      id: string,
      data: CameraConfigurationData,
      originalData: CameraConfigurationData,
    ): Promise<void> {
      state.saving = true
      try {
        const { data: response } = await hubApi.exampleStreams.updateExampleStream(
          id,
          await data.toUpdateBody(originalData),
        )
        const { getExampleStreamViewTemplates } = useExampleDefinitionsStore()
        const newCameraConfiguration = CameraConfiguration.fromResponse(response)
        newCameraConfiguration.setViewTemplates(
          getExampleStreamViewTemplates(newCameraConfiguration.definitionId),
        )
        state.cameraConfigurations = state.cameraConfigurations.map(c => {
          return c.id === newCameraConfiguration.parentExampleStreamId
            ? newCameraConfiguration
            : c
        })
      } catch (error) {
        console.error(error)
      } finally {
        state.saving = false
      }
    },
  }

  const getters = {
    getCameraConfigurationById (cameraConfigurationId: string): CameraConfiguration | undefined {
      return state.cameraConfigurations.find(cameraConfiguration => cameraConfiguration.id === cameraConfigurationId)
    },
  }

  return {
    ...toRefs(shallowReadonly(state)),
    ...actions,
    ...getters,
  }
})
